import { memo, useReducer, useState } from 'react';
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { BsThreeDotsVertical, BsFillFileEarmarkLockFill, BsEyeFill } from 'react-icons/bs';
import PropTypes from 'prop-types';
import { Button } from 'ypf-pas-general-ui-components';

import baseStyle from '../../styles/base.module.scss';
import { assignmentTypeEnum } from '../../../../utils/dataStatus';
import { finishService } from '../../../../services/workReport';
import useToast from '../../../../hooks/useToast';
import useDetailsEventsModal from '../../../../hooks/useDetailsEventsModal';

import styles from './LocationProvider.module.scss';

/**
 * @typedef Props
 * @property {assignmentTypeEnum[keyof assignmentTypeEnum]} assignmentType
 * @property {string} groupID
 * @property {(id: string) => void} handleGroup
 * @property {() => void} handleReloadTable
 * @property {boolean} isAvailableToFinish
 * @property {boolean} isChecked
 * @property {boolean} isDisabled
 * @property {boolean} isInspector
 * @property {number} requestID
 * @property {import("../../hooks/useSort").SortType} sortType
 * @property {string} title
 * @property {string} titleSortBy
 */

/** @param {Props} */
const LocationProvider = ({
  assignmentType,
  groupID,
  handleGroup,
  handleReloadTable,
  isAvailableToFinish,
  isChecked,
  isDisabled,
  isInspector,
  requestID,
  sortType,
  title,
  titleSortBy,
  requestData
}) => {
  const {
    handleShowDetailsEventsModal,
    handleOperations,
    handleHasTechnicalLocations,
    handleTitleModal
  } = useDetailsEventsModal();
  const [showActionsModal, toggleActionsModal] = useReducer((state) => !state, false);
  const [isFinishingService, setIsFinishingService] = useState(false);
  const { setNewToast } = useToast();

  const handleCheckBox = () => {
    handleGroup(groupID);
  };

  const handleFinishService = () => {
    setIsFinishingService(true);
    finishService({
      isMonthly: assignmentType === assignmentTypeEnum.Mensualizada,
      requestID
    })
      .then(() => handleReloadTable())
      .catch((error) => setNewToast({ type: 'ERROR', message: error.message }))
      .finally(() => setIsFinishingService(false));
  };
  const handleSetTitleModal = () => {
    handleTitleModal({
      title: requestData.nroSolicitud
    });
  };

  return (
    <tr>
      <td colSpan={8} data-row-col={2}>
        <div className={styles['header-container']}>
          <div className={styles['location-provider-container']}>
            <div className={styles['location-provider-checkbox']}>
              <input
                checked={isChecked}
                className={baseStyle['focus-ring']}
                disabled={isDisabled}
                onChange={handleCheckBox}
                type="checkbox"
              />
            </div>
            <div className={styles['details-container']}>
              {sortType === 'provider' ? (
                <>
                  {!!titleSortBy && (
                    <>
                      {titleSortBy.length > 25 ? (
                        <OverlayTrigger
                          delay={{ show: 250, hide: 400 }}
                          overlay={<Tooltip id="sort-title-tooltip">{titleSortBy}</Tooltip>}
                          placement="top"
                        >
                          <span className={styles.description}>{titleSortBy}</span>
                        </OverlayTrigger>
                      ) : (
                        <span className={styles.description}>{titleSortBy}</span>
                      )}
                    </>
                  )}
                  {!!title && (
                    <>
                      {title.length > 20 ? (
                        <OverlayTrigger
                          delay={{ show: 250, hide: 400 }}
                          overlay={<Tooltip id="title-tooltip">{title}</Tooltip>}
                          placement="top"
                        >
                          <span className={styles.description}>{title}</span>
                        </OverlayTrigger>
                      ) : (
                        <span className={styles.description}>{title}</span>
                      )}
                    </>
                  )}
                  <>
                    {handleHasTechnicalLocations({ headData: requestData }) && (
                      <Button
                        className="eye-button"
                        colorIcon="primary"
                        icon={BsEyeFill}
                        onClick={() => {
                          handleSetTitleModal();
                          handleShowDetailsEventsModal({ showModal: true });
                          handleOperations({ requestData, isFromVE: true });
                        }}
                        sizeIcon="20px"
                        type="button"
                        widthButton="40px"
                      />
                    )}
                  </>
                </>
              ) : (
                <>
                  <span className={styles.title}>Ubicación</span>
                  {titleSortBy.length > 25 ? (
                    <OverlayTrigger
                      delay={{ show: 250, hide: 400 }}
                      overlay={<Tooltip id="sort-title-tooltip">{titleSortBy}</Tooltip>}
                      placement="top"
                    >
                      <span className={styles.description}>{titleSortBy}</span>
                    </OverlayTrigger>
                  ) : (
                    <span className={styles.description}>{titleSortBy}</span>
                  )}
                  {!!title && (
                    <>
                      <span className={styles.title}>Equipo</span>
                      {title.length > 20 ? (
                        <OverlayTrigger
                          delay={{ show: 250, hide: 400 }}
                          overlay={<Tooltip id="title-tooltip">{title}</Tooltip>}
                          placement="top"
                        >
                          <span className={styles.description}>{title}</span>
                        </OverlayTrigger>
                      ) : (
                        <span className={styles.description}>{title}</span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {isInspector && isAvailableToFinish && sortType === 'provider' && (
              <OverlayTrigger
                onToggle={toggleActionsModal}
                overlay={
                  <Popover
                    className={styles.popover}
                    id="popover-positioned-left"
                    onClick={toggleActionsModal}
                  >
                    <Popover.Body>
                      <button
                        className={styles['btn-finish-service']}
                        disabled={isFinishingService}
                        onClick={handleFinishService}
                        type="button"
                      >
                        <BsFillFileEarmarkLockFill className={styles['icon-finish-service']} />{' '}
                        Finalizar servicio
                      </button>
                    </Popover.Body>
                  </Popover>
                }
                placement="left"
                rootClose
                show={showActionsModal}
                trigger="click"
              >
                <button
                  aria-label="Acciones"
                  className={`${baseStyle['focus-ring']} ${styles.btn}`}
                  type="button"
                >
                  <BsThreeDotsVertical />
                </button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

LocationProvider.propTypes = {
  assignmentType: PropTypes.number.isRequired,
  groupID: PropTypes.number.isRequired,
  handleGroup: PropTypes.func.isRequired,
  isAvailableToFinish: PropTypes.bool.isRequired,
  isChecked: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  isInspector: PropTypes.bool.isRequired,
  handleReloadTable: PropTypes.func.isRequired,
  requestID: PropTypes.number.isRequired,
  sortType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleSortBy: PropTypes.string.isRequired,
  requestData: PropTypes.shape({ nroSolicitud: PropTypes.string })
};
LocationProvider.defaultProps = {
  requestData: {}
};

export default memo(
  LocationProvider,
  (prev, next) => prev.isChecked === next.isChecked && prev.isDisabled === next.isDisabled
);
